import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation, Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import type { Children } from '@repo/shared/types';
import { ModalHeader, Modal, Button } from '@repo/ui';
import { useIsMobile } from '@repo/mui-config/hooks';

import { MODAL_STATE, type ModalStateUnion } from '@/components/modal/modal.store';
import { useModalWithLocalFallback } from '@/components/modal/use-modal-with-local-fallback';
import { ClickableTypography } from '@/components/dashboard/components/loan-action-card/loan-action-card-builder/clickable-typography';

interface AgreementsProps extends Children {
  title: string;
  link?: string;
  type?: ModalStateUnion;
  modalBodyChildren?: ReactNode;
}

export function AgreementsWrapper({
  title,
  link,
  children,
  type,
  modalBodyChildren,
}: AgreementsProps) {
  const { t } = useTranslation();
  const { open, isLocalModalOpen, closeLocalModal } = useModalWithLocalFallback();
  const isMobile = useIsMobile();

  const handleOpenModal = () => {
    if (type) {
      open(type, {
        additionalContent: modalBodyChildren,
        useLocalWhenOpen: type === MODAL_STATE.LOAN_AGREEMENTS && Boolean(modalBodyChildren),
      });
    }
  };

  return (
    <>
      <Stack
        alignItems={title !== 'components.agreements.checkAll' ? 'flex-start' : 'center'}
        direction="row"
      >
        <Box>{children}</Box>
        {title !== 'components.agreements.checkAll' ? (
          <div>
            <Typography component="span" fontSize={11}>
              <Trans
                components={
                  link
                    ? [
                        <ClickableTypography
                          key="link"
                          perform={() => window.open(link, '_blank')}
                          sx={{
                            display: 'contents',
                            width: 'auto',
                            fontSize: 11,
                          }}
                        />,
                      ]
                    : []
                }
              >
                {title}
              </Trans>
            </Typography>
            {type ? (
              <Button base onClick={handleOpenModal}>
                <Typography
                  component="span"
                  display="inline"
                  sx={{ ml: 1 }}
                  variant="body2"
                  fontSize={11}
                >
                  {t('components.agreements.openModalButton')}
                </Typography>
              </Button>
            ) : null}
          </div>
        ) : (
          <Typography component="span" variant="body2" fontSize={12} lineHeight={1}>
            {t('components.agreements.checkAll')}
          </Typography>
        )}
      </Stack>
      {modalBodyChildren ? (
        <Modal
          slotProps={{
            paper: {
              sx: {
                borderRadius: '10px',
                position: 'absolute',
              },
            },
          }}
          isOpen={isLocalModalOpen}
          maxWidth="sm"
          closeModal={closeLocalModal}
        >
          <Box sx={{ py: 4, px: isMobile ? '20px' : '44px' }}>
            <ModalHeader
              centeredLogo={false}
              closeButton={{ isVisible: true, onClick: closeLocalModal }}
              sx={{ mb: '20px' }}
            />
            <Box data-testid="agreements-read-more-local">{modalBodyChildren}</Box>
          </Box>
        </Modal>
      ) : null}
    </>
  );
}
